import React, { useState, useContext, useEffect } from 'react'
import LoadingOverlay from 'react-loading-overlay'
import {
    Card,
    CardBody,
    Button,
    Row,
    Col,
    Input,
    UncontrolledAlert
} from 'reactstrap'
import ConfigContext from './ConfigContext'
import SaveConfirmationModal from './SaveConfirmationModal'

const RuleEditor = ({ rule, configType, onSave, onCancel }) => {
    const {
        loading,
        getConfigMetadata,
        getFieldMappings,
        getFieldOptions,
        getOperatorsForField,
        convertRulesToExpression,
        updateConfigData,
        configData,
        getDefaultVarsForConfigType
    } = useContext(ConfigContext)

    const [showSaveConfirmation, setShowSaveConfirmation] = useState(false)
    const [pendingConfigData, setPendingConfigData] = useState(null)
    const [errorMsg, setErrorMsg] = useState('')
    const [staticValue, setStaticValue] = useState(false)
    const [originalRule, setOriginalRule] = useState(null) // Store original rule for comparison
    const [ruleStructure, setRuleStructure] = useState({
        return_type: 'boolean',
        true_conditions: {
            any: []
        },
        false_conditions: { any: [] },
        default: false
    })

    const configMetadata = getConfigMetadata(configType?.type)
    const fieldMappings = getFieldMappings(configType?.type)
    const fieldOptions = getFieldOptions(configType?.type)

    useEffect(() => {

        // Store the original rule on first load for comparison later
        if (rule && !originalRule) {
            setOriginalRule(JSON.parse(JSON.stringify(rule))) // Deep copy to prevent reference issues
        }

        // Handle rules for both old ALWAYS format and new EXPR format
        if (rule && ((rule.type === 'EXPR' && rule.rules) || (rule.type === 'ALWAYS' && rule.rules))) {
            setRuleStructure(rule.rules)
        }
        // Handle literal values (both LIT and ALWAYS without rules)
        else if (rule && (rule.type === 'LIT' || (rule.type === 'ALWAYS' && !rule.rules))) {
            // Handle both old format (boolean/string 'TRUE') and new format (string 'True')
            setStaticValue(rule.value === true || rule.value === 'TRUE' || rule.value === 'True')
            // Reset rule structure for literal values
            setRuleStructure({
                return_type: 'boolean',
                true_conditions: { any: [] },
                false_conditions: { any: [] },
                default: false
            })
        }
    }, [rule, originalRule])

    const updateCondition = (section, anyIndex, allIndex, field, value) => {
        const newStructure = { ...ruleStructure }
        newStructure[`${section}_conditions`].any[anyIndex].all[allIndex][field] = value
        setRuleStructure(newStructure)
    }

    const addCondition = (section, anyIndex) => {
        const newStructure = { ...ruleStructure }
        newStructure[`${section}_conditions`].any[anyIndex].all.push({
            field: '', op: '', value: ''
        })
        setRuleStructure(newStructure)
    }

    const removeCondition = (section, anyIndex, allIndex) => {
        const newStructure = { ...ruleStructure }
        if (newStructure[`${section}_conditions`].any[anyIndex].all.length > 1) {
            newStructure[`${section}_conditions`].any[anyIndex].all.splice(allIndex, 1)
            setRuleStructure(newStructure)
        }
    }

    const addOrGroup = (section) => {
        const newStructure = { ...ruleStructure }
        const hasTrue = newStructure.true_conditions.any.some(group =>
            group.all.some(cond => cond.field && cond.op && cond.value)
        )
        const hasFalse = newStructure.false_conditions.any.some(group =>
            group.all.some(cond => cond.field && cond.op && cond.value)
        )

        if (section === 'true' && hasFalse) {
            setErrorMsg('Cannot add TRUE groups when FALSE groups exist. Please remove FALSE groups first.')
            return
        }
        if (section === 'false' && hasTrue) {
            setErrorMsg('Cannot add FALSE groups when TRUE groups exist. Please remove TRUE groups first.')
            return
        }

        newStructure[`${section}_conditions`].any.push({
            all: [{ field: '', op: '', value: '' }]
        })
        setRuleStructure(newStructure)
    }

    const removeOrGroup = (section, anyIndex) => {
        const newStructure = { ...ruleStructure }
        newStructure[`${section}_conditions`].any.splice(anyIndex, 1)
        setRuleStructure(newStructure)
    }

    const handleSave = () => {

        // Clear any previous error messages
        setErrorMsg('')

        const hasTrueConditions = ruleStructure.true_conditions.any.some(anyGroup =>
            anyGroup.all.some(condition => condition.field && condition.op && condition.value)
        )
        const hasFalseConditions = ruleStructure.false_conditions.any.some(anyGroup =>
            anyGroup.all.some(condition => condition.field && condition.op && condition.value)
        )


        const hasEmptyFields = (hasTrueConditions && ruleStructure.true_conditions.any.some(anyGroup =>
            anyGroup.all.some(condition =>
                condition.field || condition.op || condition.value ? (!condition.field || !condition.op || !condition.value) : false
            )
        )) || (hasFalseConditions && ruleStructure.false_conditions.any.some(anyGroup =>
                anyGroup.all.some(condition =>
            condition.field || condition.op || condition.value ? (!condition.field || !condition.op || !condition.value) : false
        )
    ))

        if (hasEmptyFields) {
            setErrorMsg('Please fill all condition fields')
            return
        }

        try {
            // Prepare the new configuration data
            const newConfigData = prepareConfigData()

            // Check if there are actual changes - compare with stored original rule
            const newVar = newConfigData.vars?.find(v => v.name === rule.name)


            // More intelligent change detection
            let hasChanges = false

            if (!originalRule || !newVar) {
                hasChanges = true
            } else {

                // Compare type changes
                if (originalRule.type !== newVar.type) {
                    hasChanges = true
                }
                // Compare value/expression changes
                else if (originalRule.value !== newVar.value) {
                    hasChanges = true
                }
                // Compare rules structure if both have rules
                else if (originalRule.rules && newVar.rules) {
                    const originalRulesStr = JSON.stringify(originalRule.rules)
                    const newRulesStr = JSON.stringify(newVar.rules)
                    if (originalRulesStr !== newRulesStr) {
                        hasChanges = true
                    }
                }
                // If one has rules and other doesn't
                else if (!!originalRule.rules !== !!newVar.rules) {
                    hasChanges = true
                }
            }


            if (!hasChanges) {
                setErrorMsg('No changes detected to save')
                return
            }

            setPendingConfigData(newConfigData)
            setShowSaveConfirmation(true)
        } catch (error) {
            console.error('Error in handleSave:', error)
            setErrorMsg(`Error preparing save: ${error.message}`)
        }
    }

    const prepareConfigData = () => {

        const hasTrueConditions = ruleStructure.true_conditions.any.some(anyGroup =>
            anyGroup.all.some(condition => condition.field && condition.op && condition.value)
        )
        const hasFalseConditions = ruleStructure.false_conditions.any.some(anyGroup =>
            anyGroup.all.some(condition => condition.field && condition.op && condition.value)
        )


        const updatedConfigData = { ...configData }

        const ruleIndex = updatedConfigData.vars.findIndex(v => v.name === rule.name)

        if (ruleIndex !== -1) {
            // Get the standard notes from defaults to preserve them
            const defaultVars = getDefaultVarsForConfigType(configType)
            const standardNotes = defaultVars.find(v => v.name === rule.name)?.notes

            if (!hasTrueConditions && !hasFalseConditions) {
                // Convert JavaScript boolean to Python boolean literal
                const pythonBooleanValue = staticValue ? 'True' : 'False'
                updatedConfigData.vars[ruleIndex] = {
                    ...updatedConfigData.vars[ruleIndex],
                    type: 'LIT',
                    value: pythonBooleanValue,
                    notes: standardNotes || updatedConfigData.vars[ruleIndex].notes // Preserve standard notes
                }
                delete updatedConfigData.vars[ruleIndex].rules
            } else {
                const processedRuleStructure = JSON.parse(JSON.stringify(ruleStructure))

                if (processedRuleStructure.true_conditions?.any) {
                    processedRuleStructure.true_conditions.any.forEach(anyGroup => {
                        anyGroup.all?.forEach(condition => {
                            if (condition.op === 'in' && typeof condition.value === 'string') {
                                if (condition.value.includes(',')) {
                                    condition.value = condition.value.split(',').map(v => v.trim()).filter(v => v !== '')
                                } else {
                                    condition.value = [condition.value]
                                }
                            }
                        })
                    })
                }

                if (processedRuleStructure.false_conditions?.any) {
                    processedRuleStructure.false_conditions.any.forEach(anyGroup => {
                        anyGroup.all?.forEach(condition => {
                            if (condition.op === 'in' && typeof condition.value === 'string') {
                                if (condition.value.includes(',')) {
                                    condition.value = condition.value.split(',').map(v => v.trim()).filter(v => v !== '')
                                } else {
                                    condition.value = [condition.value]
                                }
                            }
                        })
                    })
                }

                const expression = convertRulesToExpression(processedRuleStructure)
                updatedConfigData.vars[ruleIndex] = {
                    ...updatedConfigData.vars[ruleIndex],
                    type: 'EXPR',
                    value: expression,
                    rules: processedRuleStructure,
                    notes: standardNotes || updatedConfigData.vars[ruleIndex].notes // Preserve standard notes
                }
            }
        } else {
        }

        return updatedConfigData
    }

    const handleConfirmSave = async () => {
        setShowSaveConfirmation(false)
        try {
            await updateConfigData(configType.type, pendingConfigData)
            onSave()
        } catch (error) {
            console.error('Save error:', error)
            setErrorMsg('Error saving rule: ' + error.message)
        }
    }

    const renderValueInput = (condition, section, anyIndex, allIndex) => {
        const fieldDisplayName = Object.keys(fieldMappings).find(
            key => fieldMappings[key] === condition.field
        )

        if (!fieldDisplayName || !configMetadata) {
            return (
                <Input bsSize="sm"
                    type="text"
                    placeholder="Enter value"
                    value={condition.value || ''}
                    onChange={(e) => updateCondition(section, anyIndex, allIndex, 'value', e.target.value)}
                />
            )
        }

        const fieldMeta = configMetadata.fields.find(f => f.displayName === fieldDisplayName)

        if (!fieldMeta) {
            return (
                <Input bsSize="sm"
                    type="text"
                    placeholder="Enter value"
                    value={condition.value || ''}
                    onChange={(e) => updateCondition(section, anyIndex, allIndex, 'value', e.target.value)}
                />
            )
        }

        if (condition.op === 'in') {
            return (
                <Input bsSize="sm"
                    type="text"
                    placeholder={fieldMeta.isMultiple ? "Enter comma-separated values" : "Enter value"}
                    value={Array.isArray(condition.value) ? condition.value.join(', ') : (condition.value || '')}
                    onChange={(e) => {
                        const inputValue = e.target.value
                        updateCondition(section, anyIndex, allIndex, 'value', inputValue)
                    }}
                />
            )
        }

        if (fieldMeta.inputType === 'dropdown' && Array.isArray(fieldMeta.validValues)) {
            return (
                <Input bsSize="sm" type="select"
                    value={condition.value || ''}
                    onChange={(e) => updateCondition(section, anyIndex, allIndex, 'value', e.target.value)}>
                    <option value="">Select {fieldMeta.displayName}</option>
                    {fieldMeta.validValues.map(option => (
                        <option key={option} value={option}>{option}</option>
                    ))}
                </Input>
            )
        }

        return (
            <Input bsSize="sm"
                type="text"
                placeholder={`Enter ${fieldMeta.displayName.toLowerCase()}`}
                value={condition.value || ''}
                onChange={(e) => updateCondition(section, anyIndex, allIndex, 'value', e.target.value)}
            />
        )
    }

    const getAvailableOperators = (fieldDisplayName) => {
        if (!fieldDisplayName || !configType?.type) {
            return [
                { value: 'in', label: 'In' },
                { value: '=', label: 'Equals' },
                { value: '!=', label: 'Not Equals' },
                { value: '>', label: 'Greater Than' },
                { value: '<', label: 'Less Than' }
            ]
        }
        return getOperatorsForField(configType.type, fieldDisplayName)
    }

    const renderConditionGroup = (section, sectionName) => {
        if (ruleStructure[`${section}_conditions`].any.length === 0) return null

        return (
            <div className="mb-3">
                <div className="mb-2">
                    <div style={{
                        backgroundColor: '#EE5A29',
                        color: 'white',
                        padding: '6px 16px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        display: 'inline-block',
                        minWidth: '60px',
                        textAlign: 'center'
                    }}>
                        {sectionName}
                    </div>
                </div>

                {ruleStructure[`${section}_conditions`].any.map((anyGroup, anyIndex) => (
                    <div key={anyIndex} className="border rounded p-2 mb-2" style={{backgroundColor: '#f8f9fa'}}>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <small className="text-muted">
                                Condition {anyIndex + 1} {anyIndex > 0 && '(OR)'}
                            </small>
                            <div className="d-flex gap-1">
                                <Button size="sm" color="link" className="p-0 text-danger"
                                        onClick={() => removeOrGroup(section, anyIndex)}>
                                    <i className="fas fa-minus-circle"></i>
                                </Button>
                            </div>
                        </div>

                        {anyGroup.all.map((condition, allIndex) => (
                            <Row key={allIndex} className="align-items-center mb-1">
                                <Col md={2}>
                                    <Input bsSize="sm" type="select"
                                           value={Object.keys(fieldMappings).find(key =>
                                             fieldMappings[key] === condition.field) || ''}
                                           onChange={(e) => {
                                               updateCondition(section, anyIndex, allIndex, 'field', fieldMappings[e.target.value])
                                               updateCondition(section, anyIndex, allIndex, 'op', '')
                                               updateCondition(section, anyIndex, allIndex, 'value', '')
                                           }}>
                                        <option value="">Select Field</option>
                                        {fieldOptions.map(field => (
                                            <option key={field} value={field}>{field}</option>
                                        ))}
                                    </Input>
                                </Col>
                                <Col md={2}>
                                    <Input bsSize="sm" type="select"
                                           value={condition.op}
                                           onChange={(e) => {
                                               updateCondition(section, anyIndex, allIndex, 'op', e.target.value)
                                               updateCondition(section, anyIndex, allIndex, 'value', '')
                                           }}>
                                        <option value="">Select</option>
                                        {getAvailableOperators(
                                            Object.keys(fieldMappings).find(key => fieldMappings[key] === condition.field)
                                        ).map(op => (
                                            <option key={op.value} value={op.value}>{op.label}</option>
                                        ))}
                                    </Input>
                                </Col>
                                <Col md={4}>
                                    <div className="d-flex align-items-center gap-2">
                                        <div style={{flex: 1}}>
                                            {renderValueInput(condition, section, anyIndex, allIndex)}
                                        </div>

                                        {allIndex < anyGroup.all.length - 1 && anyGroup.all.length > 1 && (
                                            <div style={{
                                                backgroundColor: '#28a745',
                                                color: 'white',
                                                fontSize: '9px',
                                                padding: '2px 6px',
                                                borderRadius: '3px',
                                                fontWeight: 'bold'
                                            }}>
                                                AND
                                            </div>
                                        )}

                                        {allIndex === anyGroup.all.length - 1 && (
                                            <Button size="sm" color="link" className="p-0 text-success"
                                                    onClick={() => addCondition(section, anyIndex)}
                                                    style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>
                                                + Add
                                            </Button>
                                        )}
                                    </div>
                                </Col>
                                <Col md={4}>
                                    {anyGroup.all.length > 1 && (
                                        <Button size="sm" color="link" className="p-0 text-danger"
                                                onClick={() => removeCondition(section, anyIndex, allIndex)}>
                                            <i className="fas fa-minus-circle"></i>
                                        </Button>
                                    )}
                                </Col>
                            </Row>
                        ))}
                    </div>
                ))}

                <div className="text-end">
                    <Button size="sm" color="success" onClick={() => addOrGroup(section)}>
                        <i className="fas fa-plus-circle"></i> Add OR Condition
                    </Button>
                </div>

                <div className="mt-2 text-muted" style={{fontSize: '12px', fontStyle: 'italic'}}>
                    {section === 'true' ? 'False otherwise' : 'True otherwise'}
                </div>
            </div>
        )
    }

    const getCurrentRuleData = () => {
        // Return the stored original rule for proper before/after comparison
        return originalRule || rule
    }

    const getNewRuleData = () => {
        if (!pendingConfigData) return null
        const newVar = pendingConfigData.vars?.find(v => v.name === rule.name)
        return newVar
    }

    return (
        <>
            <SaveConfirmationModal
                isOpen={showSaveConfirmation}
                toggle={() => setShowSaveConfirmation(false)}
                currentRule={getCurrentRuleData()}
                newRule={getNewRuleData()}
                onConfirm={handleConfirmSave}
                loading={loading}
            />

            <LoadingOverlay active={loading} spinner>
                <Card className='p-2' style={{maxHeight: '70vh', overflow: 'auto'}}>
                    <div className="mb-2">
                        <h6 className='text-primary' style={{fontSize: '14px'}}>
                            {rule?.notes || rule?.name}
                        </h6>
                    </div>

                    <CardBody className="p-1">
                        {errorMsg && (
                            <div className="mb-2">
                                <UncontrolledAlert color="danger" role="alert" style={{fontSize: '12px', padding: '8px'}}>
                                    {errorMsg}
                                </UncontrolledAlert>
                            </div>
                        )}

                        <div style={{fontSize: '12px'}}>
                            {(ruleStructure.true_conditions.any.length === 0 && ruleStructure.false_conditions.any.length === 0) && (
                                <div className="mb-3">
                                    <div className="mb-3 p-3 border rounded" style={{backgroundColor: '#f8f9fa'}}>
                                        <div className="mb-2">
                                            <small className="text-muted">Literal:</small>
                                        </div>
                                        <div className="mb-3">
                                            <Input
                                                type="select"
                                                bsSize="sm"
                                                value={staticValue}
                                                onChange={(e) => setStaticValue(e.target.value === 'true')}
                                                style={{maxWidth: '150px'}}
                                            >
                                                <option value={false}>False</option>
                                                <option value={true}>True</option>
                                            </Input>
                                        </div>
                                        <div className="text-center">
                                            <small className="text-muted">Or add conditions:</small>
                                        </div>
                                        <div className="d-flex justify-content-center gap-3 mt-2">
                                            <Button size="sm" color="link" onClick={() => addOrGroup('true')}>
                                                <i className="fas fa-plus-circle" style={{color: '#EE5A29'}}></i> Add TRUE Group
                                            </Button>
                                            <Button size="sm" color="link" onClick={() => addOrGroup('false')}>
                                                <i className="fas fa-plus-circle" style={{color: '#EE5A29'}}></i> Add FALSE Group
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {renderConditionGroup('true', 'TRUE')}
                            {renderConditionGroup('false', 'FALSE')}

                            <div className="mt-3 p-2 border rounded" style={{backgroundColor: '#f1f3f4'}}>
                                <small className="text-muted">Preview:</small>
                                <div className="mt-1 p-1 bg-white border rounded" style={{fontFamily: 'monospace', fontSize: '11px'}}>
                                    {(ruleStructure.true_conditions.any.length === 0 && ruleStructure.false_conditions.any.length === 0)
                                        ? (staticValue ? 'True' : 'False')
                                        : (convertRulesToExpression(ruleStructure) || 'Build conditions above')
                                    }
                                </div>
                            </div>

                            <div className="d-flex justify-content-end gap-1 mt-3">
                                <Button size="sm" color='primary' onClick={handleSave}>Save</Button>
                                <Button size="sm" color='secondary' onClick={onCancel}>Cancel</Button>
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </LoadingOverlay>
        </>
    )
}

export default RuleEditor